import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PauseIcon, PlayIcon } from 'lucide-react';
import { Shelf, ShelfSkeleton } from '../components/Shelf';
import { TrackTable } from '../components/TrackTable';
import { Seo } from '../components/Seo';
import { Visualizer, type VisualizerStyle } from '../components/Visualizer';
import { usePlayer } from '../contexts/PlayerContext';
import { useShelves } from '../hooks/useShelves';
import { shelfSeeds } from '../data/seeds';
import { greeting } from '../utils/format';
interface HomeProps {
  visualizerStyle: VisualizerStyle;
  showHeroVisualizer: boolean;
}
export function Home({
  visualizerStyle,
  showHeroVisualizer
}: HomeProps) {
  const {
    shelves,
    isLoading,
    error
  } = useShelves();
  const {
    play,
    currentTrack,
    isPlaying,
    toggle
  } = usePlayer();
  const featured = shelves[0];
  const rotation = shelves.flatMap(shelf => shelf.tracks.slice(0, 3)).slice(0, 10);
  const heroTrack = currentTrack ?? featured?.tracks[0] ?? null;
  const startSet = () => {
    if (currentTrack) {
      toggle();
      return;
    }
    if (featured) play(featured.tracks, 0);
  };
  return <div className="px-5 pb-16 pt-8 sm:px-8 lg:px-12" data-id="element-259" data-mp="2vvlta9r9tu1s">
      <Seo title="Noctuzan" description={heroTrack ? `Now spinning: ${heroTrack.title} by ${heroTrack.artist}. Free music streaming with real tracks, pulled live from the iTunes catalogue.` : 'Noctuzan — free music streaming for night owls. Spin real tracks pulled live from the iTunes catalogue, browse curated shelves, and let the needle drop.'} url="/" data-id="element-260" data-mp="2qxkdpyt20cv2" />
      <motion.header initial={{
      opacity: 0,
      y: 14
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3,
      ease: [0.23, 1, 0.32, 1]
    }} className="flex flex-col gap-8 border-b border-ink-line pb-12 lg:flex-row lg:items-center lg:justify-between" data-id="element-261" data-mp="288dpxxdekv2e">
        <div className="max-w-xl" data-id="element-262" data-mp="1u8uspzm4r44v">
          <p className="text-sm text-muted" data-id="element-263" data-mp="1kkqnlgkg8gf0">
            Tonight&apos;s transmission · {shelves.reduce((n, shelf) => n + shelf.tracks.length, 0)} previews loaded
          </p>
          <h1 className="mt-3 font-display text-5xl leading-[1.05] text-cream sm:text-6xl" data-id="element-264" data-mp="1ok7sa7s1x9j4">
            {greeting()}.
            <span className="block italic text-gold" data-id="element-265" data-mp="1flmf6s5cmmpq">The needle&apos;s down.</span>
          </h1>
          <p className="mt-5 text-base leading-relaxed text-muted" data-id="element-266" data-mp="23b8d7c9zivw5">
            {heroTrack ? `Spinning ${heroTrack.title} — ${heroTrack.artist}, off ${heroTrack.album}.` : 'Real records, pulled live from the iTunes catalogue. Thirty seconds each — just enough to know.'}
          </p>
          <div className="mt-7 flex items-center gap-4" data-id="element-267" data-mp="1yk8imdoc8pwt">
            <button type="button" onClick={startSet} disabled={!featured && !currentTrack} className="flex items-center gap-2.5 rounded-full bg-gold px-6 py-3 text-sm font-medium text-ink-base transition-transform duration-150 ease-night hover:scale-[1.02] disabled:opacity-40 disabled:hover:scale-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft focus-visible:ring-offset-2 focus-visible:ring-offset-ink-base" data-id="element-268" data-mp="1yinnalu792xw">
              {isPlaying ? <PauseIcon className="h-4 w-4" aria-hidden="true" data-id="element-269" data-mp="kcslocoydkpt" /> : <PlayIcon className="h-4 w-4" aria-hidden="true" data-id="element-270" data-mp="3ssc9uxarocy0" />}
              {isPlaying ? 'Pause the set' : currentTrack ? 'Resume the set' : 'Start tonight’s set'}
            </button>
            {currentTrack ? <Link to="/now-playing" className="rounded-full border border-ink-line px-4 py-2.5 text-xs text-cream transition-colors duration-150 ease-night hover:border-gold/60 hover:text-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-iris-soft" data-id="element-271" data-mp="5nwcgcb5u1nm">
                Lyrics &amp; live wave
              </Link> : <p className="max-w-[14rem] text-xs leading-relaxed text-muted" data-id="element-272" data-mp="27eoh61l3lep7">
                Previews are capped at ~30 seconds by Apple.
              </p>}
          </div>
        </div>

        {showHeroVisualizer && <div className="relative flex-none self-center" data-id="element-273" data-mp="2hahsjhn2z25e">
            <Visualizer style={visualizerStyle} className="h-64 w-64 sm:h-80 sm:w-80" data-id="element-274" data-mp="2mbp0bgp841c2" />
            <p className="mt-2 text-center text-xs text-muted" data-id="element-275" data-mp="2qd0p5k09wij7">
              {isPlaying ? 'reacting to live audio' : 'paused — needle lifted'}
            </p>
          </div>}
      </motion.header>

      {error && <p role="status" className="mt-10 rounded-sm border border-ink-line bg-ink-panel px-4 py-3 text-sm text-cream" data-id="element-276" data-mp="303lml447nh0j">
          {error}
        </p>}

      <div className="mt-12" data-id="element-277" data-mp="3a0qcnf16fwmm">
        {isLoading ? shelfSeeds.slice(0, 2).map(seed => <ShelfSkeleton key={seed.id} title={seed.title} subtitle={seed.subtitle} data-id="element-278" data-mp="3e1tsw2n4zuod" />) : shelves.map(shelf => <Shelf key={shelf.id} shelf={shelf} data-id="element-279" data-mp="12f8bk1w724bc" />)}
      </div>

      {rotation.length > 0 && <section aria-labelledby="rotation" className="mt-16" data-id="element-280" data-mp="yd503d9iyzq2">
          <h2 id="rotation" className="font-display text-2xl text-cream" data-id="element-281" data-mp="yfr6h9koj96p">
            Tonight&apos;s rotation
          </h2>
          <p className="mt-0.5 text-sm text-muted" data-id="element-282" data-mp="ofqj9cjow8x8">Ten cuts, pulled across every row above.</p>
          <div className="mt-4 overflow-x-auto scroll-thin" data-id="element-283" data-mp="1m2phhrq15ox8">
            <TrackTable tracks={rotation} data-id="element-284" data-mp="1m51zg4vsfzaa" />
          </div>
        </section>}
    </div>;
}